import { useCallback, useEffect, useState } from "react";
import {
  getSavedFilters,
  createSavedFilter,
  updateSavedFilter,
  deleteSavedFilter,
} from "../services/SavedFilters";

/**
 * Loads/saves/applies Saved Filters for one page. Owns nothing about how a
 * page represents its own filter state — `applyFilterState` is the page's
 * own callback for restoring a `filterConfig` snapshot into its filter UI.
 */
export function useSavedFilters({ context, applyFilterState }) {
  const [mine, setMine] = useState([]);
  const [publicFilters, setPublicFilters] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!context) return;
    setLoading(true);
    try {
      const result = await getSavedFilters(context);
      setMine(result?.mine || []);
      setPublicFilters(result?.public || []);
    } catch (error) {
      console.error("Error fetching saved filters:", error);
    } finally {
      setLoading(false);
    }
  }, [context]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const save = async ({ name, description, visibility, filterConfig }) => {
    await createSavedFilter({
      context,
      name,
      description,
      visibility,
      filterConfig,
    });
    await refresh();
  };

  const update = async (id, payload) => {
    await updateSavedFilter(id, payload);
    await refresh();
  };

  const remove = async (id) => {
    await deleteSavedFilter(id);
    await refresh();
  };

  const apply = (savedFilter) => {
    applyFilterState?.(savedFilter.filterConfig);
  };

  return {
    mine,
    public: publicFilters,
    loading,
    refresh,
    save,
    update,
    remove,
    apply,
  };
}
